<template>
  <date-picker-dialog
    range
    v-bind:value="datePickerValue"
    v-bind:day-and-month-date="dayAndMonthDate"
    v-bind:ignore-first-change-month="ignoreFirstChangeMonth"
    v-on:input="onDatePickerDialogInput"
    v-on:change-month="onChangeMonth"
  >
    <template v-slot:trigger="{ listenerObject }">
      <div class="date-format-range" role="group">
        <label
          class="date-format-range__date"
          title="Выбрать начальную дату"
        >
          {{ labelFrom }}
          <input
            class="date-format-range__input"
            required
            type="date"
            v-bind:value="valueFrom">
        </label>
        <span v-show="hasSelectedDate">—</span>
        <label
          class="date-format-range__date"
          title="Выбрать конечную дату"
        >
          {{ labelTo }}
          <input
            class="date-format-range__input"
            required
            type="date"
            v-bind:value="valueTo">
        </label>
        <button
          class="date-format-range__select"
          type="button"
          v-bind:title="hasSelectedDate ? 'Изменить период' : 'Выбрать период'"
          v-on="listenerObject"
        >{{ hasSelectedDate ? '' : 'Выберите период' }}</button>
      </div>
    </template>
  </date-picker-dialog>
</template>

<script>
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import { convertToInputDate, formatDateToDayAndMonth, formatDate } from '@/utils/timeFormat';

/**
 * @description Название событие ввода.
 * @type {string}
 */
const NAME_EVENT_INPUT = 'input';

/**
 * @description Название события изменения месяца.
 * @type {string}
 */
const NAME_EVENT_CHANGE_MONTH = 'change-month';

/**
 * @description Компонент выбора временного периода с настраиваемым выводом даты.
 * @type {Vue}
 */
export default {
  name: 'DateFormatRangePicker',
  components: {
    DatePickerDialog,
  },
  computed: {
    /**
     * @description Правая граница.
     */
    dateTo() {
      const [from, to] = this.datePickerValue;
      return to || from;
    },
    /**
     * @description Имеет выбранную дату.
     * @type {boolean}
     */
    hasSelectedDate() {
      return this.datePickerValue.length !== 0;
    },
    /**
     * @description Заголовок левой даты.
     */
    labelFrom() {
      if (!this.datePickerValue[0]) return '';
      return this.dayAndMonthDate
        ? formatDateToDayAndMonth(this.datePickerValue[0])
        : formatDate(this.datePickerValue[0]);
    },
    /**
     * @description Заголовок правой даты.
     */
    labelTo() {
      if (!this.dateTo) return '';
      return this.dayAndMonthDate
        ? formatDateToDayAndMonth(this.dateTo)
        : formatDate(this.dateTo);
    },
    /**
     * @description Значение левой даты.
     */
    valueFrom() {
      return this.datePickerValue[0]
        ? convertToInputDate(this.datePickerValue[0])
        : '';
    },
    /**
     * @description Значение правой даты.
     */
    valueTo() {
      return this.dateTo ? convertToInputDate(this.dateTo) : '';
    },
  },
  data() {
    return {
      /**
       * @description Значение календаря.
       * @type {(Date[])}
       */
      datePickerValue: this.value,
    };
  },
  methods: {
    /**
     * @description Обработать ввод даты календаря.
     * @param {(Date[])} updatedValue Новые значения.
     * @returns {void}
     */
    onDatePickerDialogInput(updatedValue) {
      if (updatedValue.length === 2) {
        this.datePickerValue = (updatedValue.length === 2 ? updatedValue : [])
          .sort((first, second) => first.getTime() - second.getTime());
        this.$emit(NAME_EVENT_INPUT, this.datePickerValue.map(convertToInputDate));
      } else if (updatedValue.length === 1) {
        this.datePickerValue = updatedValue;
        this.$emit(NAME_EVENT_INPUT, [updatedValue[0], updatedValue[0]].map(convertToInputDate));
      } else {
        this.datePickerValue = [];
        this.$emit(NAME_EVENT_INPUT, []);
      }
    },
    /**
     * @description Обработать изменение месяца.
     * @param {string} yearAndMonth
     */
    onChangeMonth(yearAndMonth) {
      this.$emit(NAME_EVENT_CHANGE_MONTH, yearAndMonth);
    },
  },
  props: {
    value: {
      required: true,
      type: Array,
    },
    dayAndMonthDate: {
      default: false,
      type: Boolean,
    },
    ignoreFirstChangeMonth: {
      default: false,
      type: Boolean,
    },
  },
  watch: {
    /**
     * @description Обработать изменение значения.
     */
    value(updatedValue) {
      if (updatedValue.length === 0) {
        this.datePickerValue = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.date-format-range {
  position: relative;
  display: flex;
  align-items: center;
  gap: .5rem;
  width: 15.625rem;
  height: 2.5rem;
  padding: 0 0 0 .625rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.1875rem;
  background: var(--primary-background-color);
  border-radius: var(--input-border-radius);
  border: 1px solid var(--border-color);
  &::after {
    content: '';
    right: 0;
    width: 38px;
    height: 38px;
    position: absolute;
    background-color: var(--fill-color);
    background-image: url("@/assets/img/calendar-grey.svg");
    background-position: center;
    border-radius: 0 4px 4px 0;
    overflow: hidden;
  }
  & &__input {
    position: absolute;
    left: 0;
    width: 0;
    opacity: 0;
  }
  & &__select {
    position: absolute;
    left: 0;
    width: inherit;
    height: inherit;
    padding: inherit;
    text-align: inherit;
    z-index: 1;
  }
}

@media screen and (max-width: 600px) {
  .date-format-range {
    flex-wrap: wrap;
    width: min(100%, 15.625rem);
  }
}
</style>
